import {
  ArchiveBoxIcon,
  BrandsDeliveryIcon,
  CheckpointIcon,
  ClockFeatureIcon,
  ClockFilledIcon,
  CopyIcon,
  PackageOutlineIcon,
  TruckFeatureIcon,
  TruckIcon,
} from '@c/icons';
import { ChatBubbleLeftIcon } from '@heroicons/react/20/solid';
import { OrderDocument, OrderItemDocument } from '@models/order';
import { useMutation, useQuery } from '@tanstack/react-query';
import SafeImage from '@ui/SafeImage';
import Spinner from '@ui/Spinner';
import Stepper from '@ui/Stepper';
import { getProductById, getStaticMapUrl } from '@util/firestore/products';
import {
  checkDelivery,
  ShipEngineEvent,
  trackPackage,
} from '@util/firestore/shipengine';
import { formatTimestamp, isMobile } from '@util/index';
import { CarrierCode, CARRIER_CODE_TO_CARRIER } from '@util/maps/carriers';
import { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import { useToastContext } from 'context/ToastContext';
import { useEffect } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { Button } from '..';
import BaseModal from './BaseModal';
import { useRouter } from 'next/navigation';

interface OrderDetailsCheckpointProps {
  title: string;
  description: string;
  active?: boolean;
  date: Date;
}
export const OrderDetailsCheckpoint = ({
  title,
  description,
  active,
  date,
}: OrderDetailsCheckpointProps) => (
  <div className="z-10 flex items-start gap-[2rem]">
    <div className="w-[2.4rem]">
      <CheckpointIcon fill={active ? '#C41719' : '#d9d9d9'} />
    </div>
    <div className="flex grow flex-col items-start sm:whitespace-nowrap">
      <span className="text-brand-lighest-black text-[1.5rem] font-semibold leading-[2.2rem]">
        {title}
      </span>
      <span className="text-[1.3rem] text-brand-dark-gray">{description}</span>
      <span className="text-brand-gray">{date.toLocaleString()}</span>
    </div>
  </div>
);

const TrackingMap = ({
  orderItem,
  geoEvent,
}: {
  orderItem: OrderItemDocument;
  geoEvent?: ShipEngineEvent;
}) => (
  <div className="relative min-h-[24rem] w-full overflow-clip rounded-2xl">
    {orderItem.state === 0 && (
      <>
        <div className="absolute z-20 flex h-full w-full flex-col items-center justify-center">
          <ClockFeatureIcon />
          <span className="text-[1.8rem] font-semibold leading-[2rem] ">
            Awaiting Shipment
          </span>
        </div>
      </>
    )}
    {!!orderItem.state &&
      !!geoEvent &&
      geoEvent.latitude &&
      geoEvent.longitude && (
        <>
          <SafeImage
            alt="map"
            key={getStaticMapUrl(geoEvent.latitude, geoEvent.longitude)}
            src={getStaticMapUrl(geoEvent.latitude, geoEvent.longitude)}
            height={300}
            width={360}
            fill
            className="object-cover"
          />
          <div className="absolute z-20 flex h-full w-full flex-col items-center justify-center">
            <TruckFeatureIcon />
          </div>
        </>
      )}
    {orderItem.state === 1 && !geoEvent?.latitude && !geoEvent?.longitude && (
      <>
        <div className="absolute z-20 flex h-full w-full flex-col items-center justify-center">
          <ClockFeatureIcon />
          <span className="text-[1.8rem] font-semibold leading-[2rem]">
            No Location Available Yet
          </span>
          <div className="mt-4 flex h-min gap-[0.8rem] rounded-brand bg-gray-100 p-4">
            {orderItem.tracking_number ? (
              <>
                <span>Tracking Number: </span>
                <span>{orderItem.tracking_number}</span>
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(
                      orderItem.tracking_number ?? '' // we are checking if it exists above but typescript doesn't know that
                    );
                    alert('Copied to clipboard');
                  }}
                >
                  <CopyIcon />
                </button>
              </>
            ) : (
              <span>No Tracking Number Added Yet</span>
            )}
          </div>
        </div>
      </>
    )}
    {/* {orderItem.tracking_number?.startsWith('SDC') && (
      <div className="absolute bottom-0 left-[calc(50%_-_10rem)] order-3 mt-4 flex h-min gap-[0.8rem] rounded-brand bg-gray-100 p-4">
        <div className="flex gap-[0.8rem]">
          <Link
            target="_blank"
            href={`https://www.goglobalpost.com/track-detail/?t=${orderItem.tracking_number}`}
          >
            View On Global Post
          </Link>
          <FaExternalLinkAlt />
        </div>
      </div>
    )} */}
  </div>
);

interface TrackOrderModalProps {
  isOpen: boolean;
  order: OrderDocument;
  orderItem: OrderItemDocument;
  sellerId: string;
  dismiss: () => void;
}

function TrackOrderModal({
  isOpen,
  dismiss,
  order,
  orderItem,
}: TrackOrderModalProps) {
  const { userDoc, user } = useAuth();
  const { showSuccessToast } = useToastContext();
  const { openChatModalWithOrder } = useChat();
  const router = useRouter();
  const { data: product } = useQuery({
    queryKey: ['product', orderItem?.id],
    queryFn: () => getProductById(orderItem?.id!),
    enabled: !!orderItem?.id,
  });

  const trackingResponseMutation = useMutation({
    mutationKey: ['track', orderItem?.id],
    mutationFn: () =>
      trackPackage({
        carrier: orderItem?.carrier!,
        trackingNo: orderItem?.tracking_number!,
        service_code: orderItem.service_code,
      }),
    onSuccess: (data) => {
      if (orderItem)
        checkDelivery(data, order, orderItem, !!userDoc?.roles?.admin);
    },
    onError: (error) => {
      if (userDoc?.roles?.admin && error && error instanceof Error)
        alert(error.message);
    },
  });

  // const trackPackageGlobalMutation = useMutation({
  //   mutationKey: ['trackGlobal', orderItem?.id],
  //   mutationFn: () => trackPackageGlobal(orderItem?.tracking_number!),
  //   onSuccess: (data) => {
  //     if (orderItem)
  //       checkDelivery(data, order, orderItem.state, !!userDoc?.roles?.admin);
  //   },
  //   onError: (error) => {
  //     if (userDoc?.roles?.admin && error && error instanceof Error)
  //       alert(error.message);
  //   },
  // });

  const allData = [
    ...(trackingResponseMutation.data?.events ?? []),
    // ...(trackPackageGlobalMutation.data?.events ?? []),
  ];
  const estimated_delivery_date = orderItem.est_delivery_time;

  const geoEvent = allData.find((e) => !!e.latitude);

  // Ideally we would call this mutation from the "track package" button rather than in a useEffect on load, but this is ok for now.
  useEffect(() => {
    if (
      orderItem?.tracking_number &&
      isOpen &&
      !geoEvent &&
      !trackingResponseMutation.isPending &&
      !trackingResponseMutation.submittedAt
    ) {
      trackingResponseMutation.mutate();
    }
  }, [geoEvent, isOpen, orderItem?.tracking_number, trackingResponseMutation]);

  if (!product || !orderItem) return null;

  return (
    <BaseModal
      title={`Order #${order.order_num}`}
      isOpen={isOpen}
      dismiss={dismiss}
      isFullScreen={isMobile()}
    >
      <div className="flex w-[50rem] flex-col gap-y-6">
        {!!estimated_delivery_date && (
          <div className="my-2 flex items-center gap-[1.6rem] px-4 text-[1.6rem]">
            <span className="font-semibold">Estimated Delivery Date:</span>
            <span>
              {new Date(estimated_delivery_date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
        )}
        <div
          className="flex max-h-[90rem] flex-col gap-[1.6rem] overflow-x-hidden p-8  sm:min-w-[50rem]"
          style={{ zoom: 0.8 }}
        >
          {order.total_state <= 2 && (
            <Stepper
              steps={[
                {
                  id: 'order-placed',
                  icon: <PackageOutlineIcon />,
                  label: 'Order Placed',
                },
                {
                  id: 'awaiting-shipment',
                  icon: <ClockFilledIcon color="white" />,
                  label: `Awaiting Shipment`,
                },
                {
                  id: 'in-transit',
                  icon: <TruckIcon />,
                  label: 'In Transit',
                },
                {
                  id: 'delivered',
                  icon: <ArchiveBoxIcon />,
                  label: 'Package Delivered',
                },
              ]}
              currentStep={orderItem.state + 1}
            />
          )}
          {trackingResponseMutation.isPending ? (
            <div className="flex w-full justify-center ">
              <div className="h-[20rem] w-1/4">
                <Spinner />
              </div>
            </div>
          ) : (
            <>
              <TrackingMap orderItem={orderItem} geoEvent={geoEvent} />
              {!!allData.length ? (
                <div>
                  <div className="mb-[1.6rem] flex w-full items-center justify-between border-b-2 pb-4">
                    <h3 className="flex items-center gap-[1.6rem] text-[1.8rem] font-semibold leading-[2.5rem] text-brand-lightest-black">
                      <BrandsDeliveryIcon /> Tracking History
                    </h3>

                    {!!orderItem.tracking_number ? (
                      <div className="flex items-center gap-[1.6rem] text-[1.6rem]">
                        <span>
                          {
                            CARRIER_CODE_TO_CARRIER[
                              orderItem.carrier as CarrierCode
                            ]?.name
                          }
                        </span>
                        <div className="flex h-min gap-[0.8rem] rounded-brand bg-gray-100 p-4">
                          <span>{orderItem.tracking_number}</span>
                          <button
                            onClick={async () => {
                              await navigator.clipboard.writeText(
                                orderItem.tracking_number ?? '' // we are checking if it exists above but typescript doesn't know that
                              );
                              showSuccessToast('Copied to clipboard');
                            }}
                          >
                            <CopyIcon />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-[1.6rem] text-[1.6rem]">
                        <span>No Tracking Number Added Yet</span>
                      </div>
                    )}
                  </div>
                  {/* estimated delivery date */}

                  <div className="relative isolate flex max-h-[20rem] flex-col items-start gap-[2rem] overflow-y-scroll">
                    {allData.map((ev, i) => (
                      <OrderDetailsCheckpoint
                        key={i}
                        active={!i}
                        title={ev.description}
                        description={
                          ev.city_locality
                            ? `${ev.city_locality}, ${ev.state_province ?? ''}`
                            : ''
                        }
                        date={new Date(ev.occurred_at)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="mx-auto mt-4 flex h-min gap-[0.8rem] rounded-brand bg-gray-100 p-4">
                  {orderItem.tracking_number && orderItem.state === 0 ? (
                    <>
                      <span>Tracking Number: </span>
                      <span>{orderItem.tracking_number}</span>
                      <button
                        onClick={async () => {
                          await navigator.clipboard.writeText(
                            orderItem.tracking_number ?? '' // we are checking if it exists above but typescript doesn't know that
                          );
                          alert('Copied to clipboard');
                        }}
                      >
                        <CopyIcon />
                      </button>
                    </>
                  ) : (
                    <span>No Tracking Number Added Yet</span>
                  )}
                </div>
              )}
            </>
          )}

          <div className="space-y-4 rounded-brand border-2 border-brand-secondary bg-red-300/30 p-4">
            <div className="font-semibold">You&apos;re seeing everything. </div>
            <div>
              You have the same information as our support agents. If you need
              more info, please visit the shipping carrier&apos;s website.
            </div>
          </div>
          <div className="flex flex-col gap-[1.6rem] sm:flex-row">
            <Button
              text="Refresh"
              trailingIcon={<FiRefreshCw />}
              onClick={() => {
                if (!trackingResponseMutation.isPending) {
                  trackingResponseMutation.mutate();
                }
              }}
              width="fluid"
            />

            <Button
              text="Where's my package? Contact Seller"
              type="secondary"
              trailingIcon={<ChatBubbleLeftIcon width={24} height={24} />}
              onClick={() => {
                if (user?.isAnonymous) {
                  router.push('/verify-phone');
                } else {
                  dismiss();
                  openChatModalWithOrder(order, orderItem.seller_id);
                }
              }}
              width="fluid"
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
}

export default TrackOrderModal;
